import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Lock, RefreshCw, Trash2 } from "lucide-react";
import {
  adminListDossiers,
  adminListSupporters,
  adminMarkDossierDelivered,
  adminOverview,
} from "@/lib/api";

const STORAGE_KEY = "measure_xray_admin_token";

const money = (cents) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format((Number(cents) || 0) / 100);

const shortDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function AdminPage() {
  const [tokenInput, setTokenInput] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [overview, setOverview] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(false);

  const authed = !!token;

  const load = async (adminToken = token) => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const [overviewData, dossierData, supporterData] = await Promise.all([
        adminOverview(adminToken),
        adminListDossiers(adminToken),
        adminListSupporters(adminToken),
      ]);
      setOverview(overviewData || {});
      setDossiers(dossierData?.dossiers || []);
      setSupporters(supporterData?.supporters || []);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        toast.error("Admin token rejected.");
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
      } else {
        toast.error(err?.response?.data?.detail || "Admin data failed to load.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitToken = (e) => {
    e.preventDefault();
    const clean = tokenInput.trim();
    if (!clean) {
      toast.error("Enter the admin token.");
      return;
    }
    localStorage.setItem(STORAGE_KEY, clean);
    setToken(clean);
  };

  const clearToken = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setTokenInput("");
    setOverview(null);
    setDossiers([]);
    setSupporters([]);
  };

  const markDelivered = async (id) => {
    const deliveredUrl = window.prompt("Delivered report URL:");
    if (!deliveredUrl) return;
    try {
      await adminMarkDossierDelivered(token, id, deliveredUrl.trim());
      toast.success("Dossier marked delivered.");
      load(token);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not mark dossier delivered.");
    }
  };

  const statCards = useMemo(
    () => [
      { label: "Revenue MTD", value: money(overview?.revenue_mtd_cents), testId: "admin-revenue-mtd" },
      { label: "Active supporters", value: overview?.active_supporter_count ?? 0, testId: "admin-active-supporters" },
      { label: "Pending dossiers", value: overview?.pending_dossier_count ?? 0, testId: "admin-pending-dossiers" },
      { label: "Recent signups", value: overview?.recent_signup_count ?? 0, testId: "admin-recent-signups" },
    ],
    [overview]
  );

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-5 sm:px-8 py-16" data-testid="admin-page">
        <form onSubmit={submitToken} className="paper-card-elevated rounded-sm p-6" data-testid="admin-token-form">
          <Lock className="w-6 h-6 text-[hsl(var(--ink-muted))] mb-4" />
          <div className="section-label mb-2">Owner admin</div>
          <h1 className="font-serif text-3xl font-semibold mb-4">Enter admin token</h1>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            data-testid="admin-token-input"
            className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue)/0.25)] mb-4"
            placeholder="X-Admin-Token"
          />
          <button type="submit" className="pill-btn pill-btn-primary w-full justify-center" data-testid="admin-token-submit">
            Unlock dashboard
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 mb-7 flex-wrap">
        <div>
          <div className="section-label mb-2">Owner admin</div>
          <h1 className="hero-title text-4xl">Measure X-Ray admin</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load(token)}
            disabled={loading}
            className="pill-btn pill-btn-ghost disabled:opacity-60"
            data-testid="admin-refresh-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button type="button" onClick={clearToken} className="pill-btn pill-btn-ghost" data-testid="admin-clear-token">
            <Trash2 className="w-4 h-4" />
            Clear token
          </button>
        </div>
      </div>

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-overview-cards">
        {statCards.map((card) => (
          <div key={card.label} className="paper-card rounded-sm p-4" data-testid={card.testId}>
            <div className="section-label mb-2">{card.label}</div>
            <div className="font-serif text-3xl font-semibold">{card.value}</div>
          </div>
        ))}
      </section>

      <section className="paper-card-elevated rounded-sm p-5 mb-6" data-testid="admin-dossiers-section">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="section-label">Dossier requests</div>
            <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">Paid custom report queue.</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] border-b border-[hsl(var(--rule))]">
              <tr>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Request</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {dossiers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-5 text-[hsl(var(--ink-muted))]">No dossier requests yet.</td>
                </tr>
              ) : (
                dossiers.map((dossier) => (
                  <tr key={dossier.id} className="border-b border-dashed border-[hsl(var(--rule))] last:border-0" data-testid={`admin-dossier-${dossier.id}`}>
                    <td className="py-3 pr-3 align-top">
                      <span className="stamp stamp-inferred">{dossier.status}</span>
                    </td>
                    <td className="py-3 pr-3 align-top min-w-[260px]">
                      <div className="font-medium">{dossier.bill_or_measure}</div>
                      {dossier.specific_questions && (
                        <div className="text-xs text-[hsl(var(--ink-muted))] mt-1 max-w-md">{dossier.specific_questions}</div>
                      )}
                      {dossier.deadline_hint && (
                        <div className="text-xs font-mono text-[hsl(var(--accent-blue))] mt-1">Deadline: {dossier.deadline_hint}</div>
                      )}
                      {dossier.delivered_url && (
                        <a className="text-xs text-[hsl(var(--accent-blue))] ink-underline mt-1 inline-block" href={dossier.delivered_url} target="_blank" rel="noreferrer">
                          Delivered URL
                        </a>
                      )}
                    </td>
                    <td className="py-3 pr-3 align-top font-mono text-xs">{dossier.requester_email}</td>
                    <td className="py-3 pr-3 align-top text-xs text-[hsl(var(--ink-muted))]">{shortDate(dossier.created_at)}</td>
                    <td className="py-3 pr-3 align-top">
                      <button
                        type="button"
                        onClick={() => markDelivered(dossier.id)}
                        className="pill-btn pill-btn-ghost text-xs"
                        data-testid={`admin-mark-delivered-${dossier.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Mark delivered
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="paper-card rounded-sm p-5" data-testid="admin-supporters-section">
        <div className="section-label mb-4">Supporters</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] border-b border-[hsl(var(--rule))]">
              <tr>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Tier</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Anonymous</th>
                <th className="py-2 pr-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {supporters.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-5 text-[hsl(var(--ink-muted))]">No supporters yet.</td>
                </tr>
              ) : (
                supporters.map((supporter) => (
                  <tr key={supporter.id} className="border-b border-dashed border-[hsl(var(--rule))] last:border-0" data-testid={`admin-supporter-${supporter.id}`}>
                    <td className="py-3 pr-3"><span className="stamp stamp-inferred">{supporter.status}</span></td>
                    <td className="py-3 pr-3 font-mono text-xs">{supporter.tier}</td>
                    <td className="py-3 pr-3">{money(supporter.amount_cents)}</td>
                    <td className="py-3 pr-3">{supporter.anonymous ? "Yes" : supporter.display_name || "No"}</td>
                    <td className="py-3 pr-3 text-xs text-[hsl(var(--ink-muted))]">{shortDate(supporter.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
